import React, { useEffect, useState, useRef } from 'react';
import { useIntl, FormattedMessage } from 'react-intl'; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

const GoogleTranslateSelector = () => {
  const [mounted, setMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const initCalledRef = useRef(false);

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleDropdown();
    }
  };

  const handleOptionClick = (language) => {
    setIsOpen(false);
    buttonRef.current?.focus();
  };

  const handleLanguageSwitch = (lang) => {
    setIsOpen(false);
    buttonRef.current?.focus();

    if (lang === 'ca') {
      clearGoogleTranslateCookies();
      setTimeout(() => window.location.reload(), 100);
      return;
    }

    const interval = setInterval(() => {
      const select = document.querySelector('select.goog-te-combo');
      if (select) {
        select.value = lang;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        clearInterval(interval);
      }
    }, 500);
  };

  const clearGoogleTranslateCookies = () => {
    const domains = [
      window.location.hostname,
      `.${window.location.hostname.split('.').slice(-2).join('.')}`,
    ];

    ['googtrans'].forEach((cookieName) => {
      domains.forEach((domain) => {
        document.cookie = `${cookieName}=; path=/; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
        document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
      });
    });

    document.cookie.split(';').forEach((c) => {
      const cookie = c.trim().split('=');
      if (cookie[0].includes('googtrans')) {
        document.cookie = `${cookie[0]}=; path=/; expires=${new Date(0).toUTCString()};`;
      }
    });

    try {
      localStorage.removeItem('googtrans');
      sessionStorage.removeItem('googtrans');
    } catch (e) {}
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted || initCalledRef.current) return;

    const targetEl = document.getElementById('google_translate_element');
    if (!targetEl) return;

    initCalledRef.current = true;

    window.googleTranslateElementInit = () => {
      if (!document.getElementById('google_translate_element')) return;

      new window.google.translate.TranslateElement(
        {
          pageLanguage: 'ca',
          includedLanguages: 'ca,es,en,fr',
          layout: window.google.translate.TranslateElement.InlineLayout.HORIZONTAL,
        },
        'google_translate_element',
      );
    };

    if (!document.getElementById('google-translate-script')) {
      const script = document.createElement('script');
      script.id = 'google-translate-script';
      script.src =
        '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      document.body.appendChild(script);
    } else if (window.google?.translate?.TranslateElement) {
      // Script already loaded (e.g. hot-reload), init manually
      window.googleTranslateElementInit();
    }
  }, [mounted]);

  // Render the hidden div unconditionally so it exists before the
  // useEffect that initialises Google Translate runs.
  return (
    <>
      <div id="google_translate_element" className="d-none" />

      {mounted && (
        <div
          className="languageSelector d-print-none ms-md-3"
          aria-describedby="language-picker-description"
        >
          <button
            ref={buttonRef}
            className="languageSelector__toggle"
            type="button"
            id="dropdownLanguageBtn"
            onClick={toggleDropdown}
            onKeyDown={handleKeyDown}
            aria-controls="language-picker-dropdown"
            aria-haspopup="listbox"
            aria-expanded={isOpen}
          >
            <span id="language-picker-description">
              <FormattedMessage id="Traduir" defaultMessage="Traduir" />
            </span>
            <FontAwesomeIcon
              icon={faChevronDown}
              className="languageSelector__toggle__icon"
            />
          </button>

          {isOpen && (
            <ul
              id="language-picker-dropdown"
              className="languageSelector__list list-unstyled my-0"
              role="listbox"
            >
              {[
                { lang: 'ca', label: 'Català' },
                { lang: 'es', label: 'Castellano' },
                { lang: 'en', label: 'English' },
                { lang: 'fr', label: 'Français' },
              ].map(({ lang, label }) => (
                <li key={lang} role="option">
                  <button
                    data-lang={lang}
                    className="languageSelector__option language notranslate"
                    onClick={() => handleLanguageSwitch(lang)}
                    lang={lang}
                  >
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
};

export default GoogleTranslateSelector;